
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet } from "lucide-react";
import { RFPEstimate } from "@/types/project-types";
import { ComponentType } from "@/types/estimation-types";
import { pointValues, componentTypes } from "@/data/estimationData";
import { generatePDF, calculateTotals, exportToExcel } from "./rfp-utils";

interface ReportTabProps {
  formData: {
    name: string;
    description: string;
  };
  componentEstimates: RFPEstimate[];
  version?: number;
  updatedAt?: string;
}

const ReportTab: React.FC<ReportTabProps> = ({ 
  formData, 
  componentEstimates,
  version = 1,
  updatedAt = new Date().toISOString()
}) => {
  const reportRef = React.useRef<HTMLDivElement>(null);
  
  const handleGeneratePDF = () => {
    generatePDF(reportRef, formData.name);
  };
  
  const handleExportExcel = () => {
    exportToExcel(componentEstimates, formData.name, {
      projectName: formData.name,
      description: formData.description,
      version: version,
      updatedAt: updatedAt
    });
  };

  const totals = calculateTotals(componentEstimates);
  
  const sortedEstimates = [...componentEstimates].sort((a, b) => {
    const aIndex = componentTypes.indexOf(a.component_type as ComponentType);
    const bIndex = componentTypes.indexOf(b.component_type as ComponentType);
    return aIndex - bIndex;
  });

  const filteredEstimates = sortedEstimates.filter(est => est.total_points > 0);

  return (
    <div className="space-y-4 pt-4">
      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader className="border-b border-purple-100 dark:border-gray-800">
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300 flex justify-between items-center">
            <span>Estimation Report</span>
            <div className="flex gap-2">
              <Button 
                onClick={handleGeneratePDF}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                Download as PDF
              </Button>
              <Button 
                onClick={handleExportExcel}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export to Excel
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <div ref={reportRef} className="p-8 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="mb-6 border-b pb-4">
              <img src="/lovable-uploads/41b570c8-bbdf-405b-96af-d22e664c02cd.png" alt="CardyAI Logo" className="h-12 mb-4" />
              <h2 className="text-2xl font-bold text-purple-700">{formData.name}</h2>
              <p className="text-gray-600 mt-2">{formData.description || "No description provided"}</p>
              <div className="flex justify-between mt-4 text-sm">
                <div>
                  <span>Date: <span className="font-semibold">{new Date().toLocaleDateString()}</span></span>
                  <span className="ml-4">Version: <span className="font-semibold">{version}</span></span>
                </div>
                <span>Last Updated: <span className="font-semibold">{new Date(updatedAt).toLocaleDateString()}</span></span>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-purple-600">Estimation Summary</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Total Components</div>
                  <div className="text-2xl font-bold text-purple-700">
                    {totals.total_points}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Dev Days</div>
                  <div className="text-2xl font-bold text-purple-700">
                    {totals.total_dev_days.toFixed(1)}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Total Days</div>
                  <div className="text-2xl font-bold text-purple-700">
                    {totals.total_days.toFixed(1)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3 text-purple-600">Detailed Breakdown</h3>
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-purple-50">
                    <th className="p-2 text-left border border-gray-200">Component Type</th>
                    <th className="p-2 text-center border border-gray-200">Simple</th>
                    <th className="p-2 text-center border border-gray-200">Medium</th>
                    <th className="p-2 text-center border border-gray-200">Complex</th>
                    <th className="p-2 text-center border border-gray-200">Supercomplex</th>
                    <th className="p-2 text-center border border-gray-200">Total Components</th>
                    <th className="p-2 text-center border border-gray-200">Dev Days</th>
                    <th className="p-2 text-center border border-gray-200">Total Days</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEstimates.map((estimate, index) => {
                    const pointMap = pointValues[estimate.component_type as keyof typeof pointValues] || {
                      'Simple': 2,
                      'Medium': 3,
                      'Complex': 8,
                      'Supercomplex': 13
                    };
                    
                    return (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="p-2 font-medium border border-gray-200">{estimate.component_type}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.simple_count} <span className="text-xs text-gray-500">({pointMap.Simple} day{pointMap.Simple !== 1 ? 's' : ''})</span></td>
                        <td className="p-2 text-center border border-gray-200">{estimate.medium_count} <span className="text-xs text-gray-500">({pointMap.Medium} days)</span></td>
                        <td className="p-2 text-center border border-gray-200">{estimate.complex_count} <span className="text-xs text-gray-500">({pointMap.Complex} days)</span></td>
                        <td className="p-2 text-center border border-gray-200">{estimate.supercomplex_count} <span className="text-xs text-gray-500">({pointMap.Supercomplex} days)</span></td>
                        <td className="p-2 text-center font-semibold border border-gray-200">{estimate.total_points}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.total_dev_days.toFixed(1)}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.total_days.toFixed(1)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-purple-100">
                    <td className="p-2 font-bold border border-gray-200">TOTALS</td>
                    <td className="p-2 text-center font-semibold border border-gray-200">{totals.simple_count}</td>
                    <td className="p-2 text-center font-semibold border border-gray-200">{totals.medium_count}</td>
                    <td className="p-2 text-center font-semibold border border-gray-200">{totals.complex_count}</td>
                    <td className="p-2 text-center font-semibold border border-gray-200">{totals.supercomplex_count}</td>
                    <td className="p-2 text-center font-bold border border-gray-200">{totals.total_points}</td>
                    <td className="p-2 text-center font-bold border border-gray-200">{totals.total_dev_days.toFixed(1)}</td>
                    <td className="p-2 text-center font-bold border border-gray-200">{totals.total_days.toFixed(1)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="mt-8 text-sm text-gray-600 border-t pt-4">
                <p><strong>Notes:</strong></p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Story points follow the Fibonacci sequence (1, 2, 3, 5, 8, 13, 21)</li>
                  <li>Points directly map to days - 1 point equals 1 day of total effort</li>
                  <li>Points vary by component type to account for different complexity levels</li>
                  <li>Development effort is approximately 40% of the total estimate</li>
                  <li>Testing effort is approximately 30% of the total estimate</li>
                  <li>UAT effort is approximately 20% of the total estimate</li>
                  <li>Project Management effort is approximately 10% of the total estimate</li>
                  <li>Estimates include discovery, development, testing, and bug fixing</li>
                  <li>This is a preliminary estimate for discussion purposes only</li>
                </ul>
              </div>
              
              <div className="mt-8 pt-8 border-t text-center text-sm text-gray-500">
                © {new Date().getFullYear()} <a href="https://cardyai.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">cardyai.com</a>. All rights reserved. | v1.2.0
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportTab;
